import React, { useState, useEffect } from 'react';
import { toast } from 'components/Toast';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useContractor } from 'context/ContractorContext';
import { getContractorDocuments, downloadContractorDocument, getContractorProfile } from 'services/contractorPortal';

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return val; }
};

const ContractorDocuments = () => {
    const { contractorId } = useContractor();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'my-documents' ? 1 : 0);
    const [selectedYear, setSelectedYear] = useState('');
    const [downloading, setDownloading] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(1);

    // My Documents (admin-uploaded)
    const [myDocs, setMyDocs] = useState([]);
    const [myDocsLoading, setMyDocsLoading] = useState(false);
    const [myPage, setMyPage] = useState(1);
    const [myRows, setMyRows] = useState(10);
    const [myDownloading, setMyDownloading] = useState(null);

    const { data: profile } = useQuery({
        queryKey: ['cp-profile-docs', contractorId],
        queryFn: () => getContractorProfile(contractorId),
        enabled: !!contractorId,
    });

    useEffect(() => {
        if (activeTab !== 1 || !profile?.email) return;
        setMyDocsLoading(true);
        const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
        fetch(`${ONBOARDING_API}/api/employee-documents?email=${encodeURIComponent(profile.email)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(d => setMyDocs(Array.isArray(d) ? d : []))
            .catch(() => setMyDocs([]))
            .finally(() => setMyDocsLoading(false));
    }, [activeTab, profile?.email]);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const { data: documents = [], isLoading } = useQuery({
        queryKey: ['cp-documents', contractorId, selectedYear],
        queryFn: () => getContractorDocuments(contractorId, selectedYear || undefined),
        enabled: !!contractorId,
    });

    const totalPages = Math.ceil(documents.length / rowsPerPage);
    const paginated = documents.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const handleDownload = async (doc) => {
        setDownloading(doc.id);
        try {
            const result = await downloadContractorDocument(doc.id);
            if (result?.url) {
                window.open(result.url, '_blank');
            } else {
                toast('Download link not available.', 'warning');
            }
        } catch {
            toast('Failed to download document.', 'error');
        } finally {
            setDownloading(null);
        }
    };

    const myPaginated = myDocs.slice((myPage - 1) * myRows, myPage * myRows);
    const myTotalPages = Math.ceil(myDocs.length / myRows);

    const tabStyle = (active) => ({
        padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
        borderBottom: active ? '2px solid #1a9fbb' : '2px solid transparent',
        background: 'transparent', color: active ? '#1a9fbb' : '#6c757d',
    });

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Documents</h2>
            <p style={{ color: '#6c757d', marginBottom: 16 }}>View and download your tax documents and files shared by your employer.</p>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', marginBottom: 20, background: '#fff', borderRadius: '8px 8px 0 0', padding: '0 4px' }}>
                <button style={tabStyle(activeTab === 0)} onClick={() => setActiveTab(0)}>📋 Tax Documents</button>
                <button style={tabStyle(activeTab === 1)} onClick={() => setActiveTab(1)}>📁 My Documents</button>
            </div>

            {/* Tax Documents Tab */}
            {activeTab === 0 && (
                <>
                    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>Filter by Year</label>
                        <select value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setPage(1); }}
                            style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 32px 6px 12px', fontSize: 13, color: '#333', background: '#fff', cursor: 'pointer', appearance: 'auto', minWidth: 130 }}>
                            <option value="">All Years</option>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                            <thead>
                                <tr style={{ background: '#f0f9fb' }}>
                                    {['Document', 'Type', 'Jurisdiction', 'Year', 'Filed On', 'Action'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Loading...</td></tr>
                                ) : documents.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '32px', textAlign: 'center' }}>
                                            <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
                                            <div style={{ color: '#555', fontWeight: 600, marginBottom: 4 }}>No tax documents found</div>
                                            <div style={{ color: '#aaa', fontSize: 13 }}>1099-NEC documents will appear here once generated.</div>
                                        </td>
                                    </tr>
                                ) : paginated.map((doc, i) => (
                                    <tr key={doc.id || i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500 }}>{doc.title || doc.label || doc.name || '1099-NEC'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                            <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>
                                                {doc.type || doc.category || 'Tax Document'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{doc.jurisdiction || doc.state || '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{doc.taxYear || doc.year || '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{doc.createdAtUtc || doc.filedAt || doc.createdAt ? new Date(doc.createdAtUtc || doc.filedAt || doc.createdAt).toLocaleDateString() : '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                            <button onClick={() => handleDownload(doc)} disabled={downloading === doc.id}
                                                style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                                                {downloading === doc.id ? '...' : 'Download'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, fontSize: 13, color: '#6c757d' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>Rows per page:</span>
                                <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                                    style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 28px 4px 10px', fontSize: 13, color: '#333', background: '#fff', cursor: 'pointer', appearance: 'auto', minWidth: 64 }}>
                                    {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span>{documents.length === 0 ? '0 of 0' : `${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, documents.length)} of ${documents.length}`}</span>
                            <div style={{ display: 'flex', gap: 2 }}>
                                {[['«', () => setPage(1), page === 1], ['‹', () => setPage(p => Math.max(1, p - 1)), page === 1],
                                  ['›', () => setPage(p => Math.min(totalPages, p + 1)), page === totalPages || totalPages === 0],
                                  ['»', () => setPage(totalPages), page === totalPages || totalPages === 0]].map(([label, action, disabled]) => (
                                    <button key={label} onClick={action} disabled={disabled}
                                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #dee2e6', background: disabled ? '#f8f9fa' : '#fff', color: disabled ? '#bbb' : '#495057', cursor: disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* My Documents Tab */}
            {activeTab === 1 && (
                <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 380 }}>
                        <thead>
                            <tr style={{ background: '#f0f9fb' }}>
                                {['File Name', 'Type', 'Uploaded On', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {myDocsLoading ? (
                                <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>⏳ Loading...</td></tr>
                            ) : myPaginated.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ padding: '32px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 36, marginBottom: 8 }}>📁</div>
                                        <div style={{ color: '#555', fontWeight: 600, marginBottom: 4 }}>No documents yet</div>
                                        <div style={{ color: '#aaa', fontSize: 13 }}>Documents shared by your employer will appear here.</div>
                                    </td>
                                </tr>
                            ) : myPaginated.map((doc) => (
                                <tr key={doc.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500 }}>
                                        <span style={{ marginRight: 6 }}>📄</span>
                                        {doc.fileName || doc.label || doc.name || `Document #${doc.id}`}
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <span style={{ background: '#e8f4f8', color: '#1a9fbb', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>
                                            {doc.documentType || doc.category || 'Document'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(doc.uploadedAt || doc.createdAt)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <button onClick={async () => {
                                            setMyDownloading(doc.id);
                                            try {
                                                const url = doc.fileUrl || doc.url;
                                                if (url) { window.open(url, '_blank'); return; }
                                                const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
                                                const res = await fetch(`${ONBOARDING_API}/api/employee-documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
                                                const data = await res.json();
                                                if (data?.url) window.open(data.url, '_blank');
                                                else toast('Download link not available.', 'warning');
                                            } catch { toast('Failed to download.', 'error'); }
                                            finally { setMyDownloading(null); }
                                        }} disabled={myDownloading === doc.id}
                                            style={{ background: myDownloading === doc.id ? '#ccc' : '#1B75F0', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                                            {myDownloading === doc.id ? '...' : 'Download'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, fontSize: 13, color: '#6c757d' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>Rows per page:</span>
                            <select value={myRows} onChange={e => { setMyRows(Number(e.target.value)); setMyPage(1); }}
                                style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 28px 4px 10px', fontSize: 13, color: '#333', background: '#fff', cursor: 'pointer', appearance: 'auto', minWidth: 64 }}>
                                {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <span>{myDocs.length === 0 ? '0 of 0' : `${(myPage - 1) * myRows + 1}–${Math.min(myPage * myRows, myDocs.length)} of ${myDocs.length}`}</span>
                        <div style={{ display: 'flex', gap: 2 }}>
                            {[['«', () => setMyPage(1), myPage === 1], ['‹', () => setMyPage(p => Math.max(1, p - 1)), myPage === 1],
                              ['›', () => setMyPage(p => Math.min(myTotalPages, p + 1)), myPage === myTotalPages || myTotalPages === 0],
                              ['»', () => setMyPage(myTotalPages), myPage === myTotalPages || myTotalPages === 0]].map(([label, action, disabled]) => (
                                <button key={label} onClick={action} disabled={disabled}
                                    style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #dee2e6', background: disabled ? '#f8f9fa' : '#fff', color: disabled ? '#bbb' : '#495057', cursor: disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractorDocuments;

